import React, { useContext, useEffect, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import { ApiContext } from '@api/api.tsx';
import styled from 'styled-components';
import { LovWithoutIdWithFillingDataDTO } from '@api/ringil-api.v2.tsx';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';
import { isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils.tsx';
import MultipleValues from '@components/ringil3/HelperComponents/MultipleValues.tsx';
import SvgYellowExclamation from '@components/ringil3/icons/YellowExclamation.tsx';
import SvgTagPlus from '@components/ringil3/icons/TagPlus.tsx';
import SvgTagEdit from '@components/ringil3/icons/TagEdit.tsx';
import { toTestIdSlug } from '@utils/testUtils';

interface ComponentProps {
  secured?: SecuredConfig;
  valueCodes: string[];
  valueNames?: string[];
  onChange: (v: string[]) => void;
  readOnly?: boolean;
  required?: boolean;
  errorMessage?: string;
  companyId?: string;
  prefillEnabled?: boolean;
  dataTestId?: string;
}

interface SpecialRequirementsProps {}

const RequirementsTag: React.FC<ComponentProps> = ({
  secured,
  valueCodes,
  valueNames,
  onChange,
  readOnly = false,
  required = false,
  errorMessage,
  companyId = null,
  prefillEnabled = false,
  dataTestId,
}) => {
  const { tr } = useTranslationLgs();
  const { lovSpecialRequirementsWithParentData } = useContext(ApiContext);
  const [allRequirements, setAllRequirements] = useState<LovWithoutIdWithFillingDataDTO[]>();
  const [internalValue, setInternalValue] = useState(valueCodes);
  const acRef = useRef<AbortController>();

  useEffect(() => setInternalValue(valueCodes), [valueCodes]);

  useEffect(() => {
    acRef.current = new AbortController();

    lovSpecialRequirementsWithParentData(companyId, d => {
      if (acRef.current?.signal?.aborted) return;
      setAllRequirements(d);

      // Prefill company defaults only when empty, so a seeded value (e.g. duplication) is preserved, not clobbered.
      if (prefillEnabled && !(valueCodes?.length > 0)) {
        const preselectedIds = d.filter(requirement => requirement.isSelected).map(requirement => requirement.code);

        setInternalValue(preselectedIds);
        onChange(preselectedIds);
      }
    });

    return () => {
      acRef.current?.abort();
      acRef.current = null;
    };
  }, [companyId]);

  if (isNullOrUndefined(allRequirements)) return <></>;

  const SpecialRequirements: React.FC<SpecialRequirementsProps> = () => {
    return (
      <>
        <Grid>
          {allRequirements?.map((req, index) => {
            const checkboxTestId = dataTestId ? `${dataTestId}-${toTestIdSlug(req.name)}` : undefined;
            return (
              <InputCheckbox
                key={index}
                value={isNotNullOrUndefined(internalValue?.find(t => t === req.code))}
                disabled={req.isDisabled}
                onChange={(e, checked) => {
                  if (req.isDisabled) return;
                  if (checked) {
                    setInternalValue([...(internalValue || []), req.code]);
                  } else {
                    setInternalValue([...(internalValue || []).filter(t => t !== req.code)]);
                  }
                }}
                label={req.name}
                dataTestId={checkboxTestId}
              />
            );
          })}
        </Grid>
      </>
    );
  };

  const renderValue = () => {
    if (!(valueCodes?.length > 0)) return null;
    const values = valueNames?.length ? valueNames : allRequirements?.filter(t => valueCodes.indexOf(t.code) >= 0).map(t => t.name);
    return <MultipleValues values={values}></MultipleValues>;
  };

  return (
    <>
      <ButtonTag
        secured={secured}
        icon={valueCodes?.length > 0 ? <SvgYellowExclamation /> : null}
        label={tr(`RequirementsTag.specialRequirements`, `Carrier requirements`)}
        value={renderValue()}
        actionIcon={valueCodes?.length > 0 ? <SvgTagEdit /> : <SvgTagPlus />}
        onSave={onSuccess => {
          onChange(internalValue);
          onSuccess();
        }}
        autoClose
        readonly={readOnly}
        required={required}
        errorMessage={errorMessage}
        dataTestId={dataTestId}
      >
        <SpecialRequirements />
      </ButtonTag>
    </>
  );
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: 1.4rem;
  column-gap: 2rem;
`;

export default RequirementsTag;
